import React from 'react';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import { PriceUtils } from '@wix/bookings-uou-mappers';
import ShoppingBagFill from 'wix-ui-icons-common/on-stage/ShoppingBagFill';
import Text, { TextType } from '../../../Text/Text';
import { SelectedProduct, V3Product } from '../../../../../utils/state/types';
import { ServicePaymentDetails } from '../../../../../types/types';
import { TagName } from '../../../../../utils/tagName.const';
import { useFormComponentContext } from '../../../Hooks/useFormComponentContext';
import { ExperimentsConsts } from '../../../../../consts/experiments';
import { SingleSlotDetailsDataHook } from './dataHooks';
import { classes, st } from './SelectedRelatedProducts.st.css';

export type SelectedRelatedProductsProps = {
  selectedProductAddons?: SelectedProduct[];
  relatedProducts?: V3Product[];
  paymentDetails: ServicePaymentDetails;
};

export const SelectedRelatedProducts: React.FC<
  SelectedRelatedProductsProps
> = ({ selectedProductAddons, relatedProducts, paymentDetails }) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const { dateRegionalSettingsLocale } = useFormComponentContext();

  const isRelatedProductsOnServicesEnabled = experiments.enabled(
    ExperimentsConsts.RelatedProductsOnServices,
  );

  if (
    !isRelatedProductsOnServicesEnabled ||
    !selectedProductAddons?.length ||
    !relatedProducts?.length
  ) {
    return null;
  }

  const rows = selectedProductAddons
    .map(({ productId, variantId, quantity }) => {
      const product = relatedProducts.find((p) => p._id === productId);
      if (!product) {
        return null;
      }
      const variant = product.variantsInfo?.variants?.find(
        (v) => v._id === variantId,
      );
      const unitPrice = parseFloat(
        variant?.price?.actualPrice?.amount ??
          product.actualPriceRange?.minValue?.amount ??
          '0',
      );
      const formattedPrice =
        unitPrice > 0
          ? PriceUtils.getFormattedCurrency({
              price: unitPrice,
              currency: paymentDetails.currency,
              locale: dateRegionalSettingsLocale,
            })
          : undefined;
      return {
        key: `${productId}-${variantId}`,
        name: product.name ?? '',
        quantity,
        price: formattedPrice,
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);

  if (rows.length === 0) {
    return null;
  }

  return (
    <div
      data-hook={SingleSlotDetailsDataHook.SELECTED_RELATED_PRODUCTS}
      className={st(classes.root, { isMobile })}
    >
      <div className={classes.header}>
        <ShoppingBagFill className={classes.headerIcon} />
        <Text type={TextType.Secondary} tagName={TagName.Span}>
          {t('app.payment.summary.products.text')}
        </Text>
      </div>
      <div className={classes.items}>
        {rows.map((row) => (
          <div
            key={row.key}
            className={classes.item}
            data-hook={SingleSlotDetailsDataHook.SELECTED_RELATED_PRODUCT_ITEM}
          >
            <Text type={TextType.Secondary} tagName={TagName.Paragraph}>
              {`${row.quantity > 1 ? `${row.quantity}x ` : ''}${row.name}${
                row.price ? ` (${row.price})` : ''
              }`}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
};
